window.PremiumCollectionList =
  window.PremiumCollectionList ||
  class extends HTMLElement {
  constructor() {
    super();
    this.viewport = this.querySelector('[data-premium-viewport]');
    this.track = this.querySelector('[data-premium-track]');
    this.dragState = null;
    this.autoplayFrame = null;
    this.resumeTimer = null;
    this.maxScrollLeft = 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.interactionPauseMs = 1800;
    this.autoplayStep = 0.65;
  }

  connectedCallback() {
    if (!this.viewport || !this.track || this.initialized) return;

    this.initialized = true;
    this.handlePointerDown = this.onPointerDown.bind(this);
    this.handlePointerMove = this.onPointerMove.bind(this);
    this.handlePointerUp = this.onPointerUp.bind(this);
    this.handleMouseEnter = this.pauseAutoplay.bind(this);
    this.handleMouseLeave = this.resumeAutoplay.bind(this);
    this.handleFocusIn = this.pauseAutoplay.bind(this);
    this.handleFocusOut = this.onFocusOut.bind(this);
    this.handleWheel = this.onWheel.bind(this);
    this.handleScroll = this.onScroll.bind(this);
    this.handleResize = this.refreshBounds.bind(this);
    this.handleReducedMotionChange = this.onReducedMotionChange.bind(this);

    this.viewport.addEventListener('pointerdown', this.handlePointerDown);
    this.viewport.addEventListener('pointermove', this.handlePointerMove);
    this.viewport.addEventListener('pointerup', this.handlePointerUp);
    this.viewport.addEventListener('pointercancel', this.handlePointerUp);
    this.viewport.addEventListener('mouseenter', this.handleMouseEnter);
    this.viewport.addEventListener('mouseleave', this.handleMouseLeave);
    this.viewport.addEventListener('focusin', this.handleFocusIn);
    this.viewport.addEventListener('focusout', this.handleFocusOut);
    this.viewport.addEventListener('wheel', this.handleWheel, { passive: false });
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });

    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleReducedMotionChange);
    } else if (this.mediaQuery.addListener) {
      this.mediaQuery.addListener(this.handleReducedMotionChange);
    }

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.track);

    this.refreshBounds();
    this.resumeAutoplay();
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.pauseAutoplay();
    clearTimeout(this.resumeTimer);
    this.resumeTimer = null;

    this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
    this.viewport.removeEventListener('pointermove', this.handlePointerMove);
    this.viewport.removeEventListener('pointerup', this.handlePointerUp);
    this.viewport.removeEventListener('pointercancel', this.handlePointerUp);
    this.viewport.removeEventListener('mouseenter', this.handleMouseEnter);
    this.viewport.removeEventListener('mouseleave', this.handleMouseLeave);
    this.viewport.removeEventListener('focusin', this.handleFocusIn);
    this.viewport.removeEventListener('focusout', this.handleFocusOut);
    this.viewport.removeEventListener('wheel', this.handleWheel);
    this.viewport.removeEventListener('scroll', this.handleScroll);
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleReducedMotionChange);
    } else if (this.mediaQuery.removeListener) {
      this.mediaQuery.removeListener(this.handleReducedMotionChange);
    }

    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.dragState = null;
    this.initialized = false;
  }

  refreshBounds() {
    this.maxScrollLeft = Math.max(this.viewport.scrollWidth - this.viewport.clientWidth, 0);

    if (this.maxScrollLeft <= 0) {
      this.pauseAutoplay();
      this.viewport.scrollLeft = 0;
      return;
    }

    if (this.autoplayEnabled && !this.isInteracting()) {
      this.resumeAutoplay();
    }
  }

  onScroll() {
    if (this.viewport.scrollLeft >= this.maxScrollLeft) {
      this.viewport.scrollLeft = this.maxScrollLeft;
    }
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.viewport.scrollLeft,
    };

    this.viewport.classList.add('is-dragging');
    this.viewport.setPointerCapture(event.pointerId);
    this.pauseAutoplay();
  }

  onPointerMove(event) {
    if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;

    const delta = event.clientX - this.dragState.startX;
    this.viewport.scrollLeft = this.dragState.startScrollLeft - delta;
  }

  onPointerUp(event) {
    if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;

    if (this.viewport.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }

    this.viewport.classList.remove('is-dragging');
    this.dragState = null;
    this.resumeAutoplay(true);
  }

  onWheel(event) {
    if (this.maxScrollLeft <= 0) return;

    const useVerticalDelta = Math.abs(event.deltaY) > Math.abs(event.deltaX);
    if (!useVerticalDelta) return;

    event.preventDefault();
    this.viewport.scrollLeft += event.deltaY;
    this.resumeAutoplay(true);
  }

  onFocusOut() {
    if (!this.contains(document.activeElement)) {
      this.resumeAutoplay();
    }
  }

  onReducedMotionChange() {
    if (this.mediaQuery.matches) {
      this.pauseAutoplay();
    } else {
      this.resumeAutoplay();
    }
  }

  isInteracting() {
    return Boolean(this.dragState);
  }

  pauseAutoplay() {
    if (this.autoplayFrame) {
      cancelAnimationFrame(this.autoplayFrame);
      this.autoplayFrame = null;
    }
  }

  resumeAutoplay(withDelay = false) {
    if (!this.autoplayEnabled || this.mediaQuery.matches || this.maxScrollLeft <= 0) return;

    this.pauseAutoplay();
    clearTimeout(this.resumeTimer);

    const start = () => {
      this.autoplayFrame = requestAnimationFrame(() => this.tickAutoplay());
    };

    if (withDelay) {
      this.resumeTimer = setTimeout(start, this.interactionPauseMs);
    } else {
      start();
    }
  }

  tickAutoplay() {
    if (!this.autoplayEnabled || this.mediaQuery.matches || this.isInteracting()) {
      this.autoplayFrame = null;
      return;
    }

    if (this.viewport.scrollLeft >= this.maxScrollLeft) {
      this.viewport.scrollLeft = 0;
    } else {
      this.viewport.scrollLeft = Math.min(this.viewport.scrollLeft + this.autoplayStep, this.maxScrollLeft);
    }

    this.autoplayFrame = requestAnimationFrame(() => this.tickAutoplay());
  }
  };

if (!customElements.get('premium-collection-list')) {
  customElements.define('premium-collection-list', window.PremiumCollectionList);
}
